import { useEffect, useMemo, useState } from 'react';
import { Text, Title } from '@tremor/react';
import { fetchSales } from '../api/drilldown';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import type { SalesTicket } from '../api/types';
import SalesAggregationView from './SalesAggregationView';
import LinePartsDrilldownModal from './LinePartsDrilldownModal';
import IconButton from './IconButton';
import Modal from './Modal';
import { BackIcon, CloseIcon } from './icons';

interface Props {
  sid: number;
  /** Display name; usually the store name from the by-store chart row. */
  storeName?: string;
  /** YYYY-MM-DD; defaults to today. */
  date?: string;
  onClose: () => void;
  /** Supplied by DrilldownHost when this modal sits on top of another (JCD-59). */
  onBack?: () => void;
}

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
});

export default function StoreDrilldownModal({ sid, storeName, date, onClose, onBack }: Props) {
  const [data, setData] = useState<SalesTicket[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drilledLine, setDrilledLine] = useState<string | null>(null);

  // Fetch the day's sales scoped to this store. The daemon already accepts a
  // sid filter so we don't pull every store's tickets just to drop them client-side.
  useEffect(() => {
    let alive = true;
    const day = date ?? localYMD();
    fetchSales({ from: day, to: day, sid, type_filter: 'all', paid_filter: 'all' })
      .then((rows) => { if (alive) setData(rows); })
      .catch((e: Error) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [date, sid]);

  const totals = useMemo(() => {
    let revenue = 0;
    if (!data) return { count: 0, revenue: 0 };
    for (const r of data) revenue += r.total_mxn;
    return { count: data.length, revenue };
  }, [data]);

  return (
    <>
    <Modal
      onClose={onClose}
      ariaLabel={`${t.storeDrill.title} — ${date ?? localYMD()} · ${t.storeDrill.store} ${sid}${storeName ? ` (${storeName})` : ''}`}
      role="dialog"
      panelClassName="max-w-6xl my-8 p-6"
    >
      <div className="flex items-start gap-4">
        {onBack && (
          <IconButton onClick={onBack} label={t.drilldown.back} size="sm">
            <BackIcon />
          </IconButton>
        )}
        <Title>
          {t.storeDrill.title} — {date ?? localYMD()} · {t.storeDrill.store} {sid}
          {storeName ? ` (${storeName})` : ''}
          <span className="text-tremor-content text-base font-normal ml-2">
            · {totals.count} {t.hourDrill.summary} · {fmtMxn.format(totals.revenue)}
          </span>
        </Title>
        <IconButton label={t.common.close} onClick={onClose} className="ml-auto">
          <CloseIcon />
        </IconButton>
      </div>

      {error && (
        <div className="rounded-tremor-default bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200 p-4 mt-4">
          {error.includes('503') ? t.errors.daemonOffline : error}
        </div>
      )}
      {!error && (
        <SalesAggregationView
          tickets={data ?? []}
          loading={!data}
          onLineClick={setDrilledLine}
        />
      )}
      {data && data.length === 0 && !error && (
        <Text className="mt-2 text-tremor-content text-sm">{t.chartToggle.empty}</Text>
      )}
    </Modal>
    {drilledLine && (
      <LinePartsDrilldownModal
        tickets={data ?? []}
        line={drilledLine}
        onClose={() => setDrilledLine(null)}
      />
    )}
    </>
  );
}
